import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import { Balancer } from 'react-wrap-balancer';
import { useGetPlansLazyQuery } from '../../../generated/graphql';
import { selectPresentMeta, updateMeta } from '../../../redux/meta';
import { selectMedicalInfoAnswers } from '../../../redux/assessment';
import { useAppDispatch } from '../../../pages/assessment';
import { Icon } from '../../icon/icon';
import { AssessmentButtons, AssessmentCard } from '../shared';

const InNetwork = () => {
  const dispatch = useAppDispatch();

  const [isContinuing, setIsContinuing] = useState(null);
  const handleContinue = () => {
    setIsContinuing(true);
    dispatch(updateMeta());
  };

  const meta = useSelector(selectPresentMeta);
  const medicalInfoAnswers = useSelector(selectMedicalInfoAnswers);
  const [getPlans, { data }] = useGetPlansLazyQuery();
  const currentPlans = data?.getPlans;
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState([
    {
      label: '',
      value: '',
      inNetwork: false,
      isOutofStateNeeded: false,
      isReferralNeeded: false,
      notes: '',
    },
  ]);

  useEffect(() => {
    const { MI_InsuranceProvider } = medicalInfoAnswers;
    if (MI_InsuranceProvider && MI_InsuranceProvider.length > 0) {
      getPlans({
        variables: { carrier: MI_InsuranceProvider[0] },
      });
    }
  }, [medicalInfoAnswers.MI_InsuranceProvider, getPlans, meta.screen]);

  useEffect(() => {
    setPlans(
      currentPlans?.map(it => ({
        label: it.plan,
        value: `${it.plan}_${it.inNetwork ? 'in-network' : 'out-of-network'}`,
        inNetwork: it.inNetwork,
        isOutofStateNeeded: it.isOutOfStateNeeded,
        isReferralNeeded: it.isReferralNeeded,
        notes: it.notes,
      }))
    );
  }, [currentPlans]);

  useEffect(() => {
    if (plans && medicalInfoAnswers.MI_InsurancePlan) {
      const plan = plans.find(
        item => item.value === medicalInfoAnswers.MI_InsurancePlan[0]
      );
      if (plan) {
        setSelectedPlan(plan);
      }
    }
  }, [plans, medicalInfoAnswers.MI_InsurancePlan]);

  if (!selectedPlan) return null;

  const { isReferralNeeded, isOutofStateNeeded, notes } = selectedPlan;

  return (
    <Box sx={{ mt: 3 }}>
      <AssessmentCard>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 1,
            mt: 4,
          }}
        >
          <Icon icon="Check" fontSize="large" color="success" />
          <Balancer>
            <Typography
              variant="h5"
              component="h2"
              align="center"
              sx={{ my: 2 }}
            >
              Great news—we accept this plan!
            </Typography>
          </Balancer>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Balancer>
              {isReferralNeeded && isOutofStateNeeded && (
                <Typography variant="body2" align="center" gutterBottom>
                  Please note, however, your insurance plan requires a referral
                  from your primary care physician. You will also need to
                  confirm your plan has out-of-state coverage if you are seeking
                  treatment outside of your home state.
                </Typography>
              )}
              {isReferralNeeded && !isOutofStateNeeded && (
                <Typography variant="body2" align="center" gutterBottom>
                  Please note, however, that your insurance plan requires a
                  referral from your primary care physician before your
                  appointment.
                </Typography>
              )}
              {!isReferralNeeded && isOutofStateNeeded && (
                <Typography variant="body2" align="center" gutterBottom>
                  Please note, however, that you will need to confirm your plan
                  has out-of-state coverage if you are seeking treatment outside
                  of your home state.
                </Typography>
              )}
            </Balancer>
            {notes?.trim() && notes.trim().length > 0 && (
              <Balancer>
                <Typography variant="body2" align="center">
                  {notes}
                </Typography>
              </Balancer>
            )}
          </Box>
        </Box>
      </AssessmentCard>
      <AssessmentButtons
        primary={{
          children: 'Continue Assessment',
          onClick: handleContinue,
          loading: isContinuing,
          disabled: isContinuing,
        }}
      />
    </Box>
  );
};

export default InNetwork;
